import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import NavBarNB from '../../components/NavBarNB';
import FooterNB from '../../components/FooterNB';
import CardNB from '../../components/ui-nb/CardNB';
import ButtonNB from '../../components/ui-nb/ButtonNB';
import IconNB from '../../components/ui-nb/IconNB';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { useTranslation } from '../../i18n';

interface EquipmentData {
  folio: string;
  type: string;
  brand: string;
  model: string;
  client: string;
}

interface PartData {
  id: string;
  description: string;
  cost: number;
}

export default function ApprovePart() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [part, setPart] = useState<PartData | null>(null);
  const [equipment, setEquipment] = useState<EquipmentData | null>(null);
  const [approved, setApproved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!id) return;

    async function fetchPart() {
      if (!isSupabaseConfigured()) {
        setError('Database configuration not available');
        setLoading(false);
        return;
      }

      try {
        const { data, error: partError } = await supabase
          .from('part_request')
          .select('*, orders(folio, equipment_type, brand, model, client_name)')
          .eq('id', id)
          .single();

        if (partError) {
          setError('Part not found');
          setLoading(false);
          return;
        }

        if (data) {
          setPart({
            id: data.id,
            description: data.description,
            cost: data.cost,
          });
          if (data.orders) {
            setEquipment({
              folio: data.orders.folio,
              type: data.orders.equipment_type,
              brand: data.orders.brand,
              model: data.orders.model,
              client: data.orders.client_name,
            });
          }
        }
      } catch {
        setError('Error loading part');
      } finally {
        setLoading(false);
      }
    }

    fetchPart();
  }, [id]);

  const handleApprove = async () => {
    if (!id || !isSupabaseConfigured()) return;

    setProcessing(true);
    try {
      const { error: updateError } = await supabase
        .from('part_request')
        .update({ status: 'approved', approved_at: new Date().toISOString() })
        .eq('id', id);

      if (updateError) {
        setError('Error approving part');
        setProcessing(false);
        return;
      }

      setApproved(true);
    } catch {
      setError('Error approving part');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--nb-bg-page)', display: 'flex', flexDirection: 'column' }}>
        <NavBarNB />
        <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="text-center">
            <div style={{ width: 32, height: 32, border: '3px solid #000000', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
            <p style={{ color: '#787878' }}>{t('common.loading')}</p>
          </div>
        </main>
        <FooterNB />
      </div>
    );
  }

  if (error || !part) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--nb-bg-page)', display: 'flex', flexDirection: 'column' }}>
        <NavBarNB />
        <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="text-center max-w-md">
            <CardNB className="mb-6" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: 64, height: 64, padding: 0, margin: '0 auto 16px' }}>
              <IconNB name="close" size={32} />
            </CardNB>
            <h1 className="text-2xl font-bold text-black mb-2">Error</h1>
            <p className="mb-6 text-nb-text-secondary">{error || 'Part not found.'}</p>
            <Link to="/en">
              <ButtonNB variant="primary" size="md">
                {t('clientEquipment.backToHome')}
              </ButtonNB>
            </Link>
          </div>
        </main>
        <FooterNB />
      </div>
    );
  }

  if (approved) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--nb-bg-page)', display: 'flex', flexDirection: 'column' }}>
        <NavBarNB />
        <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '64px 16px' }}>
          <CardNB style={{ maxWidth: 448, width: '100%', textAlign: 'center' }}>
            <CardNB className="mb-6" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: 64, height: 64, padding: 0, margin: '0 auto 16px' }}>
              <IconNB name="check" size={32} />
            </CardNB>
            <h1 className="text-2xl font-bold text-black mb-2">Part approved!</h1>
            <p className="mb-6 text-nb-text-secondary">
              The technician will be notified of your approval. We will keep you informed about the repair progress.
            </p>
            <Link to="/en">
              <ButtonNB variant="primary" size="md">
                {t('clientEquipment.backToHome')}
              </ButtonNB>
            </Link>
          </CardNB>
        </main>
        <FooterNB />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--nb-bg-page)', display: 'flex', flexDirection: 'column' }}>
      <NavBarNB />
      <main style={{ flex: 1, maxWidth: 576, width: '100%', margin: '0 auto', padding: '32px 16px' }}>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-black">{t('approvePart.title')}</h1>
          <p className="text-sm mt-1 text-nb-text-muted">
            Review the information and approve the proposed part
          </p>
        </div>

        {equipment && (
          <CardNB className="mb-6">
            <h2 className="text-sm font-medium mb-3 text-nb-text-muted">Equipment being repaired</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, fontSize: 14 }}>
              <div>
                <p className="text-nb-text-muted">Folio</p>
                <p className="font-semibold text-black">{equipment.folio}</p>
              </div>
              <div>
                <p className="text-nb-text-muted">Type</p>
                <p className="font-medium text-black">{equipment.type}</p>
              </div>
              <div>
                <p className="text-nb-text-muted">Brand / Model</p>
                <p className="font-medium text-black">
                  {equipment.brand} {equipment.model}
                </p>
              </div>
              <div>
                <p className="text-nb-text-muted">Client</p>
                <p className="font-medium text-black">{equipment.client}</p>
              </div>
            </div>
          </CardNB>
        )}

        <CardNB className="mb-6">
          <h2 className="text-sm font-medium mb-3 text-nb-text-muted">Part required</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <p className="text-sm text-nb-text-muted">Description</p>
              <p className="mt-1 text-black">{part.description}</p>
            </div>
            <div style={{ paddingTop: 16, borderTop: '2px solid #000' }}>
              <p className="text-sm text-nb-text-muted">Part cost</p>
              <p className="text-3xl font-bold mt-1 text-black">
                {formatCurrency(part.cost)}
              </p>
            </div>
          </div>
        </CardNB>

        <CardNB className="mb-6" style={{ background: 'rgba(255, 214, 0, 0.1)', border: '2px solid rgba(255, 214, 0, 0.5)' }}>
          <p className="text-sm text-black">
            By approving, you authorize the technician to proceed with the purchase and installation of the part.
          </p>
        </CardNB>

        {error && (
          <CardNB className="mb-6" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '2px solid #ef4444' }}>
            <p className="text-sm text-nb-red">{error}</p>
          </CardNB>
        )}

        <ButtonNB
          onClick={handleApprove}
          disabled={processing}
          variant="primary"
          size="lg"
          loading={processing}
          style={{ width: '100%' }}>
          {processing ? 'Processing...' : 'Approve part'}
        </ButtonNB>
      </main>
      <FooterNB />
    </div>
  );
}
